import React, { useState, useEffect, useMemo } from "react";
import { Global } from "../helpers/Global";
import useAuth from "../hooks/useAuth";
import Select from "react-select";

export const MisIntervencionesFinalizadas = () => {
  const { auth } = useAuth();
  const [ticajes, setTicajes] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [centros, setCentros] = useState([]);
  const [ordenes, setOrdenes] = useState([]);
  const [usuarios, setUsuarios] = useState([]);

  // Filtros
  const [filtroCliente, setFiltroCliente] = useState("");
  const [filtroOrden, setFiltroOrden] = useState("");
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");
  const [filtroMes, setFiltroMes] = useState("");
  const [filtroAnio, setFiltroAnio] = useState("");
  const [busquedaTexto, setBusquedaTexto] = useState("");

  // Estado UI
  const [cargando, setCargando] = useState(true);
  const [mensaje, setMensaje] = useState("");
  const [orden, setOrden] = useState({ campo: "inicio", ascendente: true });

  useEffect(() => {
    const fetchDatos = async () => {
      try {
        const [resT, resCtes, resCtrs, resOts, resUsers] = await Promise.all([
          fetch(`${Global.url}ticajes/finalizados/mios`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          }),
          fetch(`${Global.url}clientes`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          }),
          fetch(`${Global.url}centros/todos`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          }),
          fetch(`${Global.url}ordenes`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          }),
          fetch(`${Global.url}usuarios`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          }),
        ]);

        if (resT.ok && resCtes.ok && resCtrs.ok && resOts.ok && resUsers.ok) {
          const [dataT, dataCtes, dataCtrs, dataOts, dataUsers] = await Promise.all([
            resT.json(),
            resCtes.json(),
            resCtrs.json(),
            resOts.json(),
            resUsers.json(),
          ]);

          setClientes(Array.isArray(dataCtes) ? dataCtes : []);
          setCentros(Array.isArray(dataCtrs) ? dataCtrs : []);
          setOrdenes(Array.isArray(dataOts) ? dataOts : []);
          setUsuarios(Array.isArray(dataUsers) ? dataUsers : []);

          const normalizados = (Array.isArray(dataT) ? dataT : []).map((t) => ({
            ...t,
            duracionMs: new Date(t.fin) - new Date(t.inicio),
          }));
          setTicajes(normalizados);
        } else {
          setMensaje("Error al obtener datos.");
        }
      } catch (error) {
        console.error("Error en la conexión:", error);
        setMensaje("No se pudo conectar con el servidor.");
      } finally {
        setCargando(false);
      }
    };

    fetchDatos();
  }, []);

  // Maps para joins en memoria
  const mapClientes = useMemo(() => {
    const m = {};
    for (const c of clientes) m[c.id] = c.nombre;
    return m;
  }, [clientes]);

  const mapCentros = useMemo(() => {
    const m = {};
    for (const c of centros) m[c.id] = c.nombre;
    return m;
  }, [centros]);

  const mapOrdenes = useMemo(() => {
    const m = {};
    for (const o of ordenes) m[o.id] = o.titulo || `Orden ${o.id}`;
    return m;
  }, [ordenes]);

  const mapUsuarios = useMemo(() => {
    const m = {};
    for (const u of usuarios) {
      const full = [u.nombre, u.apellidos].filter(Boolean).join(" ");
      m[u.id] = full || u.nombre || `Usuario ${u.id}`;
    }
    return m;
  }, [usuarios]);

  const limpiarFiltros = () => {
    setFiltroCliente("");
    setFiltroOrden("");
    setFechaDesde("");
    setFechaHasta("");
    setFiltroMes("");
    setFiltroAnio("");
    setBusquedaTexto("");
  };

  // Filtros (todo se calcula contra los datos atómicos + maps)
  const ticajesFiltrados = ticajes.filter((t) => {
    // Aunque el endpoint ya trae “míos”, dejamos esta guardia por si cambia
    const coincideUsuario = t.usuario_id === auth?.id;

    const fechaTicaje = new Date(t.inicio);
    const anio = String(fechaTicaje.getFullYear());
    const mes = String(fechaTicaje.getMonth() + 1).padStart(2, "0");

    const coincideCliente =
      !filtroCliente || String(t.cliente_id ?? "") === String(filtroCliente);

    const coincideOrden =
      !filtroOrden || String(t.orden_id ?? "") === String(filtroOrden);

    const desdeValida = !fechaDesde || fechaTicaje >= new Date(fechaDesde);

    let fechaLimiteHasta = null;
    if (fechaHasta) {
      fechaLimiteHasta = new Date(fechaHasta);
      fechaLimiteHasta.setDate(fechaLimiteHasta.getDate() + 1);
    }
    const hastaValida = !fechaHasta || fechaTicaje < fechaLimiteHasta;

    const coincideAnio = !filtroAnio || anio === filtroAnio;
    const coincideMes = !filtroMes || mes === filtroMes;

    const textoIndexado = [
      mapClientes[t.cliente_id],
      mapCentros[t.centro_id],
      mapOrdenes[t.orden_id],
      t.comentarios,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    const coincideTexto =
      !busquedaTexto || textoIndexado.includes(busquedaTexto.toLowerCase());

    return (
      coincideUsuario &&
      coincideCliente &&
      coincideOrden &&
      desdeValida &&
      hastaValida &&
      coincideAnio &&
      coincideMes &&
      coincideTexto
    );
  });

  // Ordenación (permite ordenar por etiquetas derivadas)
  const ticajesOrdenados = [...ticajesFiltrados].sort((a, b) => {
    let valorA = a[orden.campo];
    let valorB = b[orden.campo];

    // Campos “derivados”
    if (orden.campo === "cliente") {
      valorA = mapClientes[a.cliente_id] || "";
      valorB = mapClientes[b.cliente_id] || "";
    }
    if (orden.campo === "centro") {
      valorA = mapCentros[a.centro_id] || "";
      valorB = mapCentros[b.centro_id] || "";
    }
    if (orden.campo === "orden") {
      valorA = mapOrdenes[a.orden_id] || "";
      valorB = mapOrdenes[b.orden_id] || "";
    }
    if (orden.campo === "inicio" || orden.campo === "fin") {
      valorA = new Date(a[orden.campo]);
      valorB = new Date(b[orden.campo]);
    }
    if (orden.campo === "duracion") {
      valorA = a.duracionMs;
      valorB = b.duracionMs;
    }

    if (typeof valorA === "string") valorA = valorA.toLowerCase();
    if (typeof valorB === "string") valorB = valorB.toLowerCase();

    valorA = valorA ?? "";
    valorB = valorB ?? "";

    if (valorA < valorB) return orden.ascendente ? -1 : 1;
    if (valorA > valorB) return orden.ascendente ? 1 : -1;
    return 0;
  });

  const manejarOrden = (campo) => {
    const normalizar = {
      duracion: "duracion", // usa duracionMs internamente
      cliente: "cliente",
      centro: "centro",
      orden: "orden",
      inicio: "inicio",
      fin: "fin",
      comentarios: "comentarios",
    }[campo] || campo;

    setOrden((prev) => ({
      campo: normalizar,
      ascendente: prev.campo === normalizar ? !prev.ascendente : true,
    }));
  };

  const calcularDuracion = (inicio, fin) => {
    if (!inicio || !fin) return "No disponible";

    const fechaInicio = new Date(inicio);
    const fechaFin = new Date(fin);

    if (isNaN(fechaInicio) || isNaN(fechaFin)) return "No disponible";

    const diferenciaMs = fechaFin - fechaInicio;
    const horas = Math.floor(diferenciaMs / 3600000);
    const minutos = Math.floor((diferenciaMs % 3600000) / 60000);

    return `${horas}h ${minutos}min`;
  };

  const totalMs = ticajesFiltrados.reduce(
    (acc, t) => acc + (isNaN(t.duracionMs) ? 0 : t.duracionMs),
    0
  );

  const formatearDuracion = (ms) => {
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    return `${h}h ${m}min`;
  };

  // Opciones para Selects
  const opcionesClientes = useMemo(
    () => [
      { value: "", label: "Todos" },
      ...clientes
        .map((c) => ({ value: c.id, label: c.nombre }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    ],
    [clientes]
  );

  const opcionesOrdenes = useMemo(() => {
    // Solo órdenes presentes en mis ticajes, para no inflar el combo
    const ids = new Set(ticajes.map((t) => t.orden_id).filter((x) => x != null));
    const arr = Array.from(ids).map((id) => ({
      value: id,
      label: mapOrdenes[id] || `Orden ${id}`,
    }));
    return [{ value: "", label: "Todas" }, ...arr.sort((a, b) => a.label.localeCompare(b.label))];
  }, [ticajes, mapOrdenes]);

  return (
    <div className="consulta-ticajes">
      <h2 className="centrar-titulo">Mis Intervenciones Finalizadas</h2>

      {mensaje && <p className="rojo">{mensaje}</p>}
      {cargando && <p>Cargando intervenciones...</p>}

      <div className="filtros">
        <div className="filtro">
          <label>Cliente:</label>
          <Select
            options={opcionesClientes}
            value={
              filtroCliente
                ? { value: filtroCliente, label: mapClientes[filtroCliente] || `Cliente ${filtroCliente}` }
                : { value: "", label: "Todos" }
            }
            onChange={(s) => setFiltroCliente(s?.value || "")}
            isClearable
            placeholder="Filtrar por cliente"
            className="select-cliente"
            classNamePrefix="select"
          />
        </div>

        <div className="filtro">
          <label>Orden de trabajo:</label>
          <Select
            options={opcionesOrdenes}
            value={
              filtroOrden
                ? { value: filtroOrden, label: mapOrdenes[filtroOrden] || `Orden ${filtroOrden}` }
                : { value: "", label: "Todas" }
            }
            onChange={(s) => setFiltroOrden(s?.value || "")}
            isClearable
            placeholder="Filtrar por orden"
            className="select-orden"
            classNamePrefix="select"
          />
        </div>

        <div className="filtro">
          <label>Desde:</label>
          <input type="date" value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)} />
        </div>

        <div className="filtro">
          <label>Hasta:</label>
          <input type="date" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} />
        </div>

        <div className="filtro">
          <label>Año:</label>
          <Select
            options={[
              { value: "", label: "Todos" },
              ...Array.from({ length: 16 }, (_, i) => {
                const year = 2025 + i;
                return { value: String(year), label: String(year) };
              }),
            ]}
            value={filtroAnio ? { value: filtroAnio, label: filtroAnio } : { value: "", label: "Todos" }}
            onChange={(s) => setFiltroAnio(s?.value || "")}
            isClearable
            placeholder="Año"
            className="select-anio"
            classNamePrefix="select"
          />
        </div>

        <div className="filtro">
          <label>Mes:</label>
          <select value={filtroMes} onChange={(e) => setFiltroMes(e.target.value)}>
            <option value="">Todos</option>
            {Array.from({ length: 12 }, (_, i) => (
              <option key={i + 1} value={String(i + 1).padStart(2, "0")}>
                {new Date(0, i).toLocaleString("es-ES", { month: "long" })}
              </option>
            ))}
          </select>
        </div>

        <div className="filtro">
          <label>Buscar:</label>
          <input
            type="text"
            value={busquedaTexto}
            onChange={(e) => setBusquedaTexto(e.target.value)}
            placeholder="Cliente, centro, orden o comentarios"
          />
        </div>

        <div className="filtro" style={{ alignSelf: "end" }}>
          <button className="btn-login" onClick={limpiarFiltros}>
            Limpiar filtros
          </button>
        </div>
      </div>

      <div className="table-listado-padre">
        <table className="table-listado">
          <thead>
            <tr>
              <th onClick={() => manejarOrden("cliente")}>
                Cliente {orden.campo === "cliente" ? (orden.ascendente ? "⬆️" : "⬇️") : ""}
              </th>
              <th onClick={() => manejarOrden("centro")}>
                Centro {orden.campo === "centro" ? (orden.ascendente ? "⬆️" : "⬇️") : ""}
              </th>
              <th onClick={() => manejarOrden("orden")}>
                Orden {orden.campo === "orden" ? (orden.ascendente ? "⬆️" : "⬇️") : ""}
              </th>
              <th onClick={() => manejarOrden("inicio")}>
                Inicio {orden.campo === "inicio" ? (orden.ascendente ? "⬆️" : "⬇️") : ""}
              </th>
              <th onClick={() => manejarOrden("fin")}>
                Fin {orden.campo === "fin" ? (orden.ascendente ? "⬆️" : "⬇️") : ""}
              </th>
              <th onClick={() => manejarOrden("duracion")}>
                Duración {orden.campo === "duracion" ? (orden.ascendente ? "⬆️" : "⬇️") : ""}
              </th>
              <th onClick={() => manejarOrden("comentarios")}>
                Comentarios {orden.campo === "comentarios" ? (orden.ascendente ? "⬆️" : "⬇️") : ""}
              </th>
              <th onClick={() => manejarOrden("creado_por_admin_id")}>
                Modificado por {orden.campo === "creado_por_admin_id" ? (orden.ascendente ? "⬆️" : "⬇️") : ""}
              </th>
            </tr>
          </thead>

          <tbody>
            {ticajesOrdenados.length === 0 ? (
              <tr>
                <td colSpan="8">No hay ticajes finalizados que coincidan con el filtro.</td>
              </tr>
            ) : (
              ticajesOrdenados.map((t) => (
                <tr key={t.id}>
                  <td>{mapClientes[t.cliente_id] || `Cliente ${t.cliente_id}`}</td>
                  <td>{mapCentros[t.centro_id] || `Centro ${t.centro_id}`}</td>
                  <td>{mapOrdenes[t.orden_id] || `Orden ${t.orden_id}`}</td>
                  <td>{new Date(t.inicio).toLocaleString()}</td>
                  <td>{new Date(t.fin).toLocaleString()}</td>
                  <td>{calcularDuracion(t.inicio, t.fin)}</td>
                  <td>{t.comentarios || "Sin comentarios"}</td>
                  <td>{mapUsuarios[t.creado_por_admin_id] || "-"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        <div style={{ textAlign: "right", marginTop: "10px", marginRight: "10px" }}>
          <strong>Total trabajado en este listado:</strong> {formatearDuracion(totalMs)}
        </div>
      </div>
    </div>
  );
};
